import React from 'react';
import { RobbieMark } from '../ui';
import { RankedSplit, SupplierGroup, listingKeyOf } from './listingGroups';
import SupplierPickCard from './SupplierPickCard';
import OtherSuppliersAccordion from './OtherSuppliersAccordion';
import { WizardAccessProvider, useWizardAccess } from '../../contexts/WizardAccessContext';

/**
 * One spec's suppliers: "Robbie's picks" cards for #1/#2, then the rest in the
 * other-suppliers accordion. The same list serves the line's own spec and a
 * preview of another one; a preview is read-only (no shortlist controls) and
 * its cards do not repeat the spec difference the chip above already names.
 */
interface SpecSupplierListProps {
  split: RankedSplit;
  preview?: boolean;
  tickedKeys: string[];
  onToggleListing: (listingKey: string) => void;
  onAddToFirstMessage?: (supplierName: string, note: string) => void;
  partLabel?: string;
  quantity?: number;
  targetPrice?: number;
  leadWindowWeeks?: number;
  requiredCompliance?: string[];
}

const withoutSpecNote = (group: SupplierGroup): SupplierGroup => {
  const listings = group.listings.map((s) => ({ ...s, specMismatches: [] }));
  return { ...group, listings, best: listings[0] };
};

const List: React.FC<SpecSupplierListProps> = ({
  split,
  preview = false,
  tickedKeys,
  onToggleListing,
  onAddToFirstMessage,
  ...context
}) => {
  const picks = preview ? split.picks.map(withoutSpecNote) : split.picks;
  const others = preview ? split.others.map(withoutSpecNote) : split.others;
  return (
    <>
      {picks.length > 0 && (
        <div className="flex shrink-0 flex-col gap-s3 rounded-card border border-robbie-border bg-ai-panel p-s4">
          <div className="flex flex-wrap items-center gap-x-s3 gap-y-s1 px-s1 py-[2px]">
            <RobbieMark size="xs" aria-label="Robbie" />
            <span className="text-[12.5px] font-bold text-ink">Robbie's picks</span>
            <span className="text-[11px] text-ink-muted">ranked by your criteria</span>
          </div>
          {picks.map((group) => (
            <SupplierPickCard
              key={group.key}
              supplier={group.best}
              listings={group.listings}
              tickedKeys={tickedKeys}
              onToggleListing={onToggleListing}
              shortlisted={tickedKeys.includes(listingKeyOf(group.best))}
              onToggleShortlist={() => onToggleListing(listingKeyOf(group.best))}
              onAddToFirstMessage={onAddToFirstMessage && ((note) => onAddToFirstMessage(group.name, note))}
              {...context}
            />
          ))}
        </div>
      )}
      {others.length > 0 && (
        <OtherSuppliersAccordion
          groups={others}
          hasPicksAbove={picks.length > 0}
          tickedKeys={tickedKeys}
          onToggleListing={onToggleListing}
          onAddToFirstMessage={onAddToFirstMessage}
          {...context}
        />
      )}
    </>
  );
};

export const SpecSupplierList: React.FC<SpecSupplierListProps> = (props) => {
  const access = useWizardAccess();
  if (!props.preview) return <List {...props} />;
  return (
    <WizardAccessProvider {...access} readOnly>
      <List {...props} onAddToFirstMessage={undefined} />
    </WizardAccessProvider>
  );
};

export default SpecSupplierList;
